import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, Trash2, Save } from "lucide-react";

export default function Notes() {
  const [notes, setNotes] = useState([]);
  const [active, setActive] = useState(null);
  const [draft, setDraft] = useState({ title: "", content: "", tags: [] });

  const load = async () => {
    const { data } = await api.get("/notes");
    setNotes(data);
    if (!active && data.length) {
      setActive(data[0]);
      setDraft({ title: data[0].title, content: data[0].content || "", tags: data[0].tags || [] });
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const newNote = async () => {
    const { data } = await api.post("/notes", { title: "Untitled runbook", content: "" });
    setActive(data);
    setDraft({ title: data.title, content: "", tags: [] });
    load();
  };

  const save = async () => {
    if (!active) return;
    const { data } = await api.put(`/notes/${active.id}`, {
      title: draft.title,
      content: draft.content,
      tags: draft.tags,
      project_id: active.project_id || null,
    });
    setActive(data);
    load();
  };

  const remove = async (id) => {
    await api.delete(`/notes/${id}`);
    if (active?.id === id) {
      setActive(null);
      setDraft({ title: "", content: "", tags: [] });
    }
    load();
  };

  return (
    <div className="p-8 lg:p-12">
      <header className="flex items-end justify-between mb-8">
        <div>
          <div className="label-mono text-green-400 mb-2">// notes.runbook</div>
          <h1 className="font-serif text-4xl lg:text-5xl tracking-tighter" data-testid="notes-title">Notes</h1>
          <p className="text-neutral-400 text-sm mt-2">Markdown-ish notes that read like a runbook.</p>
        </div>
        <button data-testid="notes-new-button" onClick={newNote} className="btn-primary btn-glitch flex items-center gap-2">
          <Plus size={14} /> New
        </button>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <aside className="lg:col-span-4 panel p-0" data-testid="notes-list">
          <div className="label-mono px-4 py-3 border-b border-neutral-900">all.notes</div>
          {notes.length === 0 && (
            <div className="px-4 py-10 text-center text-neutral-500 font-jet text-xs">No notes yet.</div>
          )}
          {notes.map((n) => (
            <button
              key={n.id}
              data-testid={`notes-item-${n.id}`}
              onClick={() => { setActive(n); setDraft({ title: n.title, content: n.content || "", tags: n.tags || [] }); }}
              className={`w-full text-left px-4 py-3 border-b border-neutral-900 transition ${active?.id === n.id ? "bg-red-500/5 border-l-2 border-l-red-500" : "hover:bg-neutral-900/40"}`}
            >
              <div className="text-sm truncate">{n.title}</div>
              <div className="label-mono mt-1">{(n.updated_at || "").slice(0, 16)}</div>
            </button>
          ))}
        </aside>

        <section className="lg:col-span-8 panel p-0 min-h-[60vh] flex flex-col" data-testid="notes-editor">
          {!active ? (
            <div className="flex-1 grid place-items-center text-neutral-500 font-jet text-xs">
              Select or create a note.
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-neutral-900 flex items-center gap-3">
                <input
                  data-testid="notes-title-input"
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  className="bg-transparent flex-1 outline-none font-serif text-2xl tracking-tight"
                />
                <button data-testid="notes-save-button" onClick={save} className="btn-primary text-[10px] px-3 py-2 flex items-center gap-2">
                  <Save size={12} /> Save
                </button>
                <button data-testid="notes-delete-button" onClick={() => remove(active.id)} className="btn-ghost text-[10px] px-3 py-2 flex items-center gap-2">
                  <Trash2 size={12} /> Delete
                </button>
              </div>
              <textarea
                data-testid="notes-content-input"
                value={draft.content}
                onChange={(e) => setDraft({ ...draft, content: e.target.value })}
                className="flex-1 bg-black/40 p-5 outline-none resize-none font-jet text-sm leading-relaxed"
                placeholder={`# Runbook\n\n- step 1: ante\n- step 2: deal\n- step 3: ship`}
              />
            </>
          )}
        </section>
      </div>
    </div>
  );
}
